import { Common, Fulfillment } from "@secondcloset/types";

import { getAPIFromDomain } from "../../lib/api";

export interface ProductIndexQuery {
  q?: string;
  page?: number;
  per_page?: number;
  organization_id?: string;
  product_type?: string;
  active?: boolean;
  has_errors?: boolean;
  sku?: string;
  with_stock_details?: boolean;
}

export const convertToVirtualKit = async ({
  productID,
  body,
}: {
  productID: string;
  body: {
    kit_products: Record<string, number>;
  };
}): Promise<{ recipient_email: string }> => {
  const api = getAPIFromDomain("fulfillment");
  const url = `/products/${productID}/convert_to_virtual_kit`;
  const res = await api.post(url, body);
  return res.data;
};

export const convertToBaseProduct = async ({
  productID,
}: {
  productID: string;
}): Promise<{ recipient_email: string }> => {
  const api = getAPIFromDomain("fulfillment");
  const url = `/products/${productID}/convert_to_base_product`;
  const res = await api.post(url, null);
  return res.data;
};

export const fetchProductsIndex = async (
  query: ProductIndexQuery
): Promise<{
  data: Fulfillment.Product[];
  meta: { missing_info_count: number; has_errors_count: number };
}> => {
  const api = getAPIFromDomain("fulfillment", 2);
  const res = await api.get("/products", { params: query });
  return res.data;
};

export const syncProduct = async (productID: string): Promise<Fulfillment.Product> => {
  const api = getAPIFromDomain("fulfillment");
  const res = await api.put(`/products/${productID}/resync`);
  return res.data;
};

type UpdateProductPayload = {
  productId: string;
  body: unknown;
};
export const updateProduct = async (payload: UpdateProductPayload): Promise<Fulfillment.Product> => {
  const { productId, body } = payload;
  const api = getAPIFromDomain("fulfillment");
  const res = await api.put(`/products/${productId}`, body);
  return res.data;
};

interface ProductLogsQuery {
  id: string;
  page?: number;
  per_page?: number;
  event?: Fulfillment.ActivityHistory["event"];
}

interface ExportProductLogsQuery {
  productID: string;
  date_from: string;
  date_to: string;
  facility?: string[];
}

export const fetchProductLogs = async (q: ProductLogsQuery): Promise<Fulfillment.ActivityHistory[]> => {
  const { id: productID, ...query } = q;
  const api = getAPIFromDomain("fulfillment");
  const res = await api.get(`/products/${productID}/logs`, { params: query });
  return res.data;
};

export const exportProductLogs = async (q: ExportProductLogsQuery): Promise<{ message: string }> => {
  const { productID, ...query } = q;
  const api = getAPIFromDomain("fulfillment");
  const res = await api.get(`/products/${productID}/email_logs`, {
    params: query,
  });
  return res.data;
};

export interface Inventory extends Fulfillment.Inventory {
  facility: Common.FacilityCode;
  to_be_fulfilled: number;
  channels: string[];
}
type FetchInventoryQuery = {
  productId: string;
  facilities: Common.FacilityCode[] | null;
  channels: string[] | null;
};
export const fetchInventory = async (query: FetchInventoryQuery): Promise<Inventory> => {
  const { productId, ...params } = query;
  const api = getAPIFromDomain("fulfillment");
  const { data } = await api.get(`/products/${productId}/inventory`, {
    params: params,
  });
  return { ...data, facility: query.facilities?.[0], to_be_fulfilled: data.to_be_reserved + data.reserved };
};

export const fetchInventoryChannels = async (orgId: string): Promise<{ data: Fulfillment.Channel[] }> => {
  const api = getAPIFromDomain("fulfillment");
  const res = await api.get(`/inventory_channels`, {
    params: { organization_id: orgId },
  });
  return res.data;
};
